import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Logo } from '../components/ui/Logo';
import { Dropdown } from '../components/ui/Dropdown';
import { Button } from '../components/ui/Button';
import { EyeClosed } from '../components/ui/icons/EyeClosed';
import { 
  User, 
  UserPlus,
  UserCheck,
  Globe, 
  Settings, 
  HelpCircle, 
  ExternalLink, 
  Home, 
  Menu, 
  Eye,
  Check,
  CircleDollarSign,
  Link
} from 'lucide-react';
import { getAccountDropdownItems } from '../utils/getAccountDropdownItems';
import { useUser } from '../hooks/useUser';
import { useLanguage } from '../hooks/useLanguage';

export const LoginPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { currentLanguage, switchLanguage } = useLanguage();
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [twofaRequired, setTwofaRequired] = useState(false);
  const [twofaCode, setTwofaCode] = useState('');
  const [twofaError, setTwofaError] = useState<string | null>(null);
  const [pendingUser, setPendingUser] = useState<any>(null);

  // Проверка PWA режима
  const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                (window.navigator as any).standalone === true;

  // Проверка Safe Area (только для iPhone)
  const hasSafeArea = window.matchMedia('(display-mode: standalone)').matches && 
                      getComputedStyle(document.documentElement).getPropertyValue('--safe-area-top') !== '0px';
  const safeAreaTop = getComputedStyle(document.documentElement).getPropertyValue('--safe-area-top');


  // Проверка авторизации при монтировании
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const { user_id, email, password } = JSON.parse(userData);
      navigate('/user', { state: { userId: user_id, email, password } });
    }
  }, [navigate]);

  const languageItems = [
    {
      id: 'eng',
      label: 'English',
      onClick: () => switchLanguage('ENG')
    },
    {
      id: 'rus',
      label: 'Русский',
      onClick: () => switchLanguage('RUS')
    }
  ];

  const pagesItems = [
    { id: 'home', label: t('home'), icon: Home, onClick: () => navigate('/') },
    { id: 'instructions', label: t('instructions'), icon: Settings, onClick: () => navigate('/instructions') },
    { id: 'faq', label: t('faq'), icon: HelpCircle, onClick: () => navigate('/faq') },
    { id: 'pricing', label: t('pricing'), icon: CircleDollarSign, onClick: () => navigate('/pricing') },
    { id: 'links', label: t('links'), icon: Link, onClick: () => navigate('/links') }
  ];

  const { user } = useUser();
  const accountItems = getAccountDropdownItems(t, navigate, user?.is_admin || false);

  const API_URL = process.env.REACT_APP_API_URL as string;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setTwofaError(null);
    setTwofaRequired(false);
    setPendingUser(null);
    try {
      const res = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: login, password })
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.detail && typeof data.detail === 'object' && data.detail.code) {
          if (data.detail.code === 'login_delay_active' && data.detail.delay_minutes) {
            throw new Error(t(data.detail.code, { minutes: data.detail.delay_minutes }));
          } else {
          throw new Error(t(data.detail.code));
          }
        } else {
          throw new Error(t('unknown_error'));
        }
      }
      if (data.twofa_enabled === 1) {
        setTwofaRequired(true);
        setPendingUser({ user_id: data.user_id, email: data.email, password, twofa_enabled: data.twofa_enabled });
      } else {
        localStorage.setItem('user', JSON.stringify({ user_id: data.user_id, email: data.email, password, twofa_enabled: data.twofa_enabled }));
        navigate('/user', { state: { userId: data.user_id, email: data.email, password } });
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handle2faSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTwofaError(null);
    if (!pendingUser) return;
    try {
      const res = await fetch(`${API_URL}/2fa/verify_login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: pendingUser.email, totp_code: twofaCode })
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.detail && typeof data.detail === 'object' && data.detail.code) {
          if (data.detail.code === 'login_delay_active' && data.detail.delay_minutes) {
            throw new Error(t(data.detail.code, { minutes: data.detail.delay_minutes }));
          } else {
          throw new Error(t(data.detail.code));
          }
        } else {
          throw new Error(t('unknown_error'));
        }
      }
      localStorage.setItem('user', JSON.stringify(pendingUser));
      navigate('/user', { state: { userId: pendingUser.user_id, email: pendingUser.email, password: pendingUser.password } });
    } catch (err: any) {
      setTwofaError(err.message);
    }
  };

  const handleRegisterClick = () => {
    navigate('/register');
  };

  const handleForgotPassword = async () => {
    if (!login.trim()) {
      setError('Пожалуйста, введите email');
      return;
    }

    setError(null);
    setPassword(''); // Очищаем поле пароля

    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: login }),
      });

      const data = await response.json();

      if (response.ok) {
        setError(t('password_reset_sent'));
      } else {
        const errorCode = data.detail?.code;
        let errorMessage = data.detail?.message || t('request_error');
        
        // Обрабатываем специфичные ошибки
        if (errorCode === 'rate_limit_exceeded') {
          errorMessage = t('rate_limit_20_min');
        } else if (errorCode === 'password_not_recoverable') {
          errorMessage = t('password_not_recoverable');
        } else if (errorCode === 'user_not_found') {
          errorMessage = t('account_not_registered');
        }
        
        setError(errorMessage);
      }
    } catch (error) {
      setError(t('network_error'));
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="fixed top-0 left-0 w-full z-50 bg-header border-b border-border">
        <div className="flex items-center justify-between w-full h-20 px-4">
          {/* Левая колонка */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-4">
              <Logo color="primary" className="" style={{ color: '#525252', marginTop: '1mm', width: 'clamp(48px, 5.3vw, 64px)', height: 'clamp(48px, 5.3vw, 64px)' }} />
              <div className="flex flex-col justify-center min-w-0">
                <h1 className="text-3xl font-extra-bold neon-glow whitespace-nowrap" style={{ color: '#525252' }}>
                  ForsBot
                </h1>
                <p
                  className="text-textSubtle truncate"
                  style={{ fontSize: 'clamp(8px, 2vw, 16px)' }}
                >
                  {t('main_slogan')}
                </p>
              </div>
            </div>
          </div>
          {/* Правая колонка */}
          <div className="flex items-center space-x-1 flex-shrink-0">
              <Dropdown
                id="account"
                trigger={<User className="w-5 h-5 text-iconPrimary" />}
                items={accountItems}
                align="right"
              />
              <Dropdown
                id="language"
                trigger={<span className="text-sm font-medium text-iconPrimary">{currentLanguage}</span>}
                items={languageItems}
                align="right"
              />
              <Dropdown
                id="pages"
                trigger={<Menu className="w-5 h-5 text-iconPrimary" />}
                items={pagesItems}
                align="right"
              />
          </div>
        </div>
      </header>

      <main
        className="w-full flex-1 flex flex-col py-2 sm:py-8 px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24"
        style={{ 
          paddingTop: (isPWA && hasSafeArea) ? `calc(6rem + ${safeAreaTop})` : '6rem',
          paddingLeft: '0.5cm', 
          paddingRight: '0.5cm' 
        }}
      >
        {/* Форма входа */}
        <div className="w-full max-w-md mx-auto mt-6 sm:mt-8">
          {/* Login Form */}
          <div className="bg-header border border-border rounded-lg p-4 sm:p-6 md:p-8">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-text mb-4 sm:mb-6 text-center font-sans">
              {t('login')}
            </h1>
            
            {!twofaRequired ? (
              <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                {/* Login Field */}
                <div>
                  <label htmlFor="login" className="block text-sm font-medium text-text mb-1 sm:mb-2 font-sans">
                    {t('email')}
                  </label>
                  <input
                    type="text"
                    id="login"
                    value={login}
                    onChange={(e) => setLogin(e.target.value)}
                    placeholder={t('enter_login')}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-text placeholder-textSubtle font-sans text-sm sm:text-base input-neon-focus"
                    required
                  />
                </div>

                {/* Password Field */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-text mb-1 sm:mb-2 font-sans">
                    {t('password')}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t('enter_password')}
                      className="w-full px-3 py-2 pr-10 border border-border rounded-md bg-background text-text placeholder-textSubtle font-sans text-sm sm:text-base input-neon-focus"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-textSubtle focus:outline-none active:bg-transparent hover:bg-transparent"
                    >
                      {showPassword ? (
                        <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
                      ) : (
                        <EyeClosed className="h-4 w-4 sm:h-5 sm:w-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  className="w-full mt-4 sm:mt-6 focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                  style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                >
                  {t('login_button')}
                </Button>

                {/* Forgot Password Button */}
                <Button
                  type="button"
                  onClick={handleForgotPassword}
                  className="w-full mt-3 focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                  style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                >
                  {t('forgot_password')}
                </Button>
              </form>
            ) : (
              <form onSubmit={handle2faSubmit} className="space-y-3 sm:space-y-4">
                <label className="block text-sm font-medium text-text mb-1 sm:mb-2 font-sans">
                  {t('twofa_enter_totp_code')}
                </label>
                <input
                  type="text"
                  value={twofaCode}
                  onChange={e => setTwofaCode(e.target.value)}
                  maxLength={6}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-text placeholder-textSubtle font-sans text-sm sm:text-base input-neon-focus"
                  autoFocus
                  required
                />
                <Button 
                  type="submit" 
                  className="w-full mt-4 sm:mt-6 focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                  style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                >
                  {t('twofa_verify_email_code')}
                </Button>
              </form>
            )}
            {error && <div className="text-red-500 text-center mt-4">{error}</div>}
            {twofaError && <div className="text-red-500 text-center mt-4">{twofaError}</div>}

            {/* Register Link */}
            <div className="mt-4 sm:mt-6 text-center">
              <p className="text-textSubtle font-sans text-sm sm:text-base">
                {t('no_account')}{' '}
                <button
                  onClick={handleRegisterClick}
                  className="text-primary hover:underline font-medium cursor-pointer font-sans focus:outline-none focus:ring-0 active:outline-none active:ring-0"
                  style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                >
                  {t('register_link')}
                </button>
              </p>
            </div>
          </div>
        </div>
        
      </main>

      {/* Footer */}
      <footer className="fixed bottom-0 left-0 w-full bg-header border-t border-border py-1 sm:py-8 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-base sm:text-lg text-text mb-1 sm:mb-2 font-sans">{t('footer_welcome')}</p>
          <p className="text-textSubtle font-sans text-sm sm:text-base">{t('footer_slogan')}</p>
        </div>
      </footer>

    </div>
  );
}; 